'use client';

import { Card, ChartSquare, Grid3 } from 'iconsax-react';
import { useTheme } from '@teispace/next-themes';
import { useCallback, useEffect, useMemo } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import { useFeatureFlags } from '@shared/featureManagment';
import { AgahButtonLogo, CoinBag, LogoWhite } from '../../icons';

import { MobileNavItem } from './types';
import { useMobileNavbarStore } from './useMobileNavbarStore';

export const useMobileNavbar = () => {
  const { data: navItem, setter: setNavItem } = useMobileNavbarStore();
  const theme = useTheme();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useFeatureFlags();

  const allowedRoutes = [
    '/',
    '/dashboard',
    '/agah-bank',
    '/agah-bank/follow-up',
    '/agah-bank/transfer',
    '/agah-bank/settings/reissue/follow-up',
    '/investment',
    '/assets/agah-funds',
    '/assets/etf-securities',
    '/assets/gold',
  ];

  const data = [
    {
      icon: (isActive: boolean) => <Grid3 size={24} variant={isActive ? 'Bold' : 'Outline'} />,
      route: '/dashboard',
      label: 'داشبورد',
      id: MobileNavItem.Dashboard,
      gtmid: 'Dashboard',
    },
    {
      icon: (isActive: boolean) => <ChartSquare size={24} variant={isActive ? 'Bold' : 'Outline'} />,
      route: '/investment',
      label: 'سرمایه‌گذاری',
      id: MobileNavItem.Investment,
      gtmid: 'Investment',
    },
    {
      icon: () =>
        theme.resolvedTheme === 'dark' ? (
          <LogoWhite isActive={false} className='h-[24px] w-[24px]' />
        ) : (
          <AgahButtonLogo isActive={false} className='h-[24px] w-[24px]' />
        ),
      route: '/',
      label: 'آگاه',
      gtmid: 'Agah_buttom',
      id: MobileNavItem.Home,
    },
    {
      icon: (isActive: boolean) => <Card size={24} variant={isActive ? 'Bold' : 'Outline'} />,
      route: '/agah-bank',
      label: 'آگاه کارت',
      id: MobileNavItem.Bank,
      gtmid: 'Agah_cart',
    },
    {
      icon: (isActive: boolean) => <CoinBag isActive={isActive} className='h-[24px] w-[24px]' />,
      route: '/assets',
      label: 'دارایی‌ها',
      id: MobileNavItem.Assets,
      gtmid: 'Asset_tab',
    },
  ];

  const showNav = useMemo(() => {
    if (!allowedRoutes.length) return true;

    if (searchParams.size > 0) return false;

    return allowedRoutes.includes(pathname);
  }, [pathname, searchParams, allowedRoutes]);

  const getActiveNavIdFromPath = (path: string) => {
    if (path === '/') {
      const homeItem = data.find((i) => i.route === '/');
      return homeItem?.id ?? navItem;
    }

    const subRouteCandidates = data.filter((i) => i.route !== '/');

    let longestMatchingRoute: { route: string; id: MobileNavItem } | null = null;

    for (const candidate of subRouteCandidates) {
      if (!path.startsWith(candidate.route)) continue;
      if (
        longestMatchingRoute === null ||
        candidate.route.length > longestMatchingRoute.route.length
      ) {
        longestMatchingRoute = { route: candidate.route, id: candidate.id };
      }
    }

    return longestMatchingRoute?.id ?? navItem;
  };

  useEffect(() => {
    if (!showNav) return;
    const activeId = getActiveNavIdFromPath(pathname);
    if (activeId !== navItem) {
      setNavItem(activeId);
    }
  }, [pathname, navItem, setNavItem, data, showNav]);

  const isActive = useCallback((itemId: MobileNavItem) => navItem === itemId, [navItem]);

  const handleClick = useCallback(
    (item: { id: MobileNavItem; route: string; gtmid: string }) => () => {
      setNavItem(item.id);
    },
    [setNavItem]
  );

  return { data, theme, isActive, handleClick, showNav };
};
